import React from 'react';
import styled from 'styled-components';
import vars from './vars';
import { DIALOG_Z_INDEX, fullViewportFixed, useModalStackEntry } from './ModalStack';

const Overlay = styled.div`
    ${fullViewportFixed}
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${DIALOG_Z_INDEX};
    padding: 20px;
`;

type DialogVariant = 'default' | 'warning';

const Container = styled.div<{ variant: DialogVariant }>`
    background-color: ${(p) => (p.variant === 'warning' ? vars.warningTintSoft : vars.backgroundColor)};
    border: 1px solid ${(p) => (p.variant === 'warning' ? vars.warningColor : vars.dividerColor)};
    border-radius: 8px;
    width: 100%;
    max-width: 400px;
    overflow: hidden;
    box-shadow: ${vars.modalShadow};
`;

export const DialogHeader = styled.div`
    padding: 24px 24px 12px 24px;
`;

export const DialogTitle = styled.h3`
    margin: 0;
    font-size: 1.05rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

export const DialogContent = styled.div`
    padding: 12px 24px 20px 24px;
    color: ${vars.foregroundColor};
    font-size: 0.95rem;
    line-height: 1.5;
`;

export const DialogFooter = styled.div`
    padding: 16px 24px 24px 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`;

interface DialogProps extends React.HTMLAttributes<HTMLDivElement> {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    allowCloseOnOverlay?: boolean;
    variant?: DialogVariant;
}

export function Dialog({
    isOpen,
    onClose,
    children,
    allowCloseOnOverlay = true,
    variant = 'default',
    ...rest
}: DialogProps) {
    useModalStackEntry(isOpen, 'dialog');

    if (!isOpen) return null;

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && allowCloseOnOverlay) {
            onClose();
        }
    };

    return (
        <Overlay onClick={handleOverlayClick}>
            <Container variant={variant} {...rest}>
                {children}
            </Container>
        </Overlay>
    );
}
